import { remove } from 'lodash';

/**
 * @template {string} K
 * @template V
 * @typedef {{ [P in K]: V } & Iterable<[K, V]>} Dictionary
 */

/**
 * Creates a Dictionary object. This for the most part acts like a plain object,
 *  though, like a map, is always enumerated over in order.
 *
 * It can also be iterated over, yielding its values, in order.
 *
 * New entries can be added, and their 'order' will be that of the order they
 *  were added in.
 * @template {string} K
 * @template V
 * @param {[K, V]} [entries]
 * @returns {Dictionary<K, V>}
 */
export default function dictionary(entries = []) {
    if (!Array.isArray(entries)) {
        throw new TypeError(`Dictionary parameter ${entries} is not an array.`);
    }

    // Ensure input is not mutated
    entries = [...entries];

    return new Proxy({}, {
        get(dummy, key) {
            if (key === Symbol.iterator) {
                return function*() {
                    for (const e of entries) yield e[1];
                };
            }

            return entries.find(([k]) => k === key)?.[1];
        },
        getOwnPropertyDescriptor(dummy, key) {
            const entry = entries.find(([k]) => k === key);

            if (!entry) return undefined;

            return {
                configurable: true,
                enumerable: true,
                value: entry[1],
            };
        },
        set(dummy, key, value) {
            const entry = entries.find(([k]) => k === key);

            if (entry) {
                entry[1] = value;
            }
            else {
                entries.push([key, value]);
            }
        },
        has(dummy, key) {
            return entries.some(([k]) => k === key);
        },
        ownKeys(dummy) {
            return entries.map(([k]) => k);
        },
        deleteProperty(dummy, key) {
            const oldCount = entries.length;

            remove(entries, ([k]) => k === key);

            return oldCount !== entries.length;
        },
    });
}